import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ShareOutlined as ShareIcon,
  ContentCopy as CopyIcon,
  VisibilityOutlined as ViewIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import { CommonChip, CommonButton } from "../../ui";
import {
  LOTTERY_TYPES,
  STATUS_CONFIG,
  safeFromWei,
} from "./AdminLotteryCard";
import { getNetworkCurrency } from "../../../../static/Constants";

/**
 * AdminLotteryTable - table view for lotteries
 */
const AdminLotteryTable = ({
  lotteries = [],
  onViewDetails,
  onShare,
  userRole = "admin",
}) => {
  const [copiedId, setCopiedId] = useState(null);

  const handleCopy = (e, text, id) => {
    e.stopPropagation();
    if (text) {
      navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardSubtle,
        overflow: "hidden",
        bgcolor: colors.background.paper,
      }}
    >
      <Table sx={{ minWidth: 850 }}>
        <TableHead>
          <TableRow
            sx={{
              bgcolor: colors.dashboard.tableHeader,
              "& th": {
                color: colors.slate[600],
                fontWeight: 700,
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                py: 1.75,
                borderBottom: `1px solid ${colors.dashboard.tableBorder}`,
              },
            }}
          >
            <TableCell>Lottery ID</TableCell>
            <TableCell>Game Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Prize Pool</TableCell>
            <TableCell align="right">Entry Fee</TableCell>
            <TableCell>Slots / Cap</TableCell>
            {userRole === "admin" && <TableCell>Creator</TableCell>}
            <TableCell>Schedule</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lotteries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={userRole === "admin" ? 9 : 8} sx={{ py: 6, textAlign: "center" }}>
                <Typography sx={{ color: colors.slate[400], fontWeight: 500 }}>
                  No lotteries match your selected filter criteria.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            lotteries.map((item) => {
              const typeConfig = LOTTERY_TYPES[item.lotteryType] || LOTTERY_TYPES[0];
              const statusInfo = STATUS_CONFIG[item.status] || {
                label: `Status ${item.status}`,
                variant: "neutral",
                color: colors.slate[600],
                bg: colors.slate[100],
              };
              const TypeIcon = typeConfig.icon;
              const totalPrize = safeFromWei(item.totalPrize);
              const entryFee = safeFromWei(item.entryFee);
              const currency = getNetworkCurrency(item.networkId);
              const isCreatorCopied = copiedId === `creator-${item.lotteryId}`;

              return (
                <TableRow
                  key={item.lotteryId}
                  hover
                  sx={{
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      bgcolor: colors.dashboard.tableRowHover,
                    },
                    "& td": {
                      py: 1.75,
                      borderBottom: `1px solid ${colors.dashboard.tableBorder}`,
                      fontSize: "13px",
                    },
                  }}
                >
                  {/* Lottery ID */}
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "14px" }}>
                        #{item.lotteryId}
                      </Typography>
                      <Typography sx={{ fontSize: "11px", color: colors.slate[400], fontFamily: "monospace" }}>
                        {item.deployedOn
                          ? moment.unix(item.deployedOn).format("DD/MM/YYYY")
                          : "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Game Type */}
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: borderRadiusPx.sm,
                          bgcolor: typeConfig.bgColor,
                          color: typeConfig.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <TypeIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 600, color: colors.slate[800], fontSize: "13px" }}>
                        {typeConfig.shortName}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <CommonChip
                      label={statusInfo.label}
                      variant={statusInfo.variant}
                      backgroundColor={statusInfo.bg}
                      color={statusInfo.color}
                      size="small"
                      pill
                    />
                  </TableCell>

                  {/* Prize Pool */}
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 800, color: colors.primary.main, fontSize: "14px" }}>
                      {totalPrize} <Box component="span" sx={{ fontSize: "11px", color: colors.slate[500] }}>{currency}</Box>
                    </Typography>
                  </TableCell>

                  {/* Entry Fee */}
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 600, color: colors.slate[700], fontSize: "13px" }}>
                      {entryFee} <Box component="span" sx={{ fontSize: "11px", color: colors.slate[500] }}>{currency}</Box>
                    </Typography>
                  </TableCell>

                  {/* Slots / Capacity */}
                  <TableCell>
                    <Typography sx={{ fontSize: "12px", color: colors.slate[700], fontWeight: 500 }}>
                      {item.pickNumbers ? `${item.pickNumbers} slots` : "Auto"}
                      {item.capacity ? ` / ${item.capacity} cap` : ""}
                    </Typography>
                  </TableCell>

                  {/* Creator */}
                  {userRole === "admin" && (
                    <TableCell>
                      {item.ownerAddress ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography sx={{ fontSize: "12px", fontFamily: "monospace", color: colors.slate[600] }}>
                            {`${item.ownerAddress.slice(0, 6)}...${item.ownerAddress.slice(-4)}`}
                          </Typography>
                          <Tooltip title={isCreatorCopied ? "Copied!" : "Copy Address"} arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => handleCopy(e, item.ownerAddress, `creator-${item.lotteryId}`)}
                              sx={{ p: 0.25 }}
                            >
                              {isCreatorCopied ? (
                                <CheckIcon sx={{ fontSize: 14, color: colors.emerald[600] }} />
                              ) : (
                                <CopyIcon sx={{ fontSize: 14, color: colors.slate[400] }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: "12px", color: colors.slate[400] }}>-</Typography>
                      )}
                    </TableCell>
                  )}

                  {/* Schedule */}
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontSize: "11px", color: colors.slate[600] }}>
                        Start: {item.startTime ? moment.unix(item.startTime).format("DD/MM/YY hh:mma") : "N/A"}
                      </Typography>
                      {item.lotteryType !== 0 && item.lotteryType !== "0" && (
                        <Typography sx={{ fontSize: "11px", color: colors.slate[500] }}>
                          Draw: {item.drawTime ? moment.unix(item.drawTime).format("DD/MM/YY hh:mma") : "N/A"}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      <Tooltip title="View Details" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onViewDetails(item.lotteryId)}
                          sx={{
                            color: colors.primary.main,
                            bgcolor: colors.blue[50],
                            "&:hover": { bgcolor: colors.blue[100] },
                          }}
                        >
                          <ViewIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>

                      {onShare && (
                        <Tooltip title="Share Link" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onShare(item);
                            }}
                            sx={{
                              color: colors.slate[500],
                              "&:hover": { color: colors.primary.main, bgcolor: colors.slate[100] },
                            }}
                          >
                            <ShareIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

AdminLotteryTable.propTypes = {
  lotteries: PropTypes.array.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onShare: PropTypes.func,
  userRole: PropTypes.string,
};

export default AdminLotteryTable;
